import React, { useMemo, useRef, useCallback } from "react";
import { Empty } from "antd";
import {
  SCHEDULE_ROW_HEIGHT,
  SCHEDULE_GANTT_HEADER_HEIGHT,
  SCHEDULE_DATE_LOCALE,
} from "./scheduleConstants";

const ROW_HEIGHT = SCHEDULE_ROW_HEIGHT;
const HEADER_HEIGHT = SCHEDULE_GANTT_HEADER_HEIGHT;
const BAR_HEIGHT = 16;
const MIN_TIMELINE_WIDTH = 720;

const DAY_MS = 24 * 60 * 60 * 1000;

const getScaleUnitMs = (timeScale) => {
  if (timeScale === "month") return 30 * DAY_MS;
  if (timeScale === "week") return 7 * DAY_MS;
  return DAY_MS;
};

const getTimeRange = (rows) => {
  let min = null;
  let max = null;

  rows.forEach((row) => {
    if (row.start && (!min || row.start < min)) min = row.start;
    if (row.end && (!max || row.end > max)) max = row.end;
  });

  if (!min || !max) {
    const now = new Date();
    return { min: now, max: new Date(now.getTime() + 30 * DAY_MS) };
  }

  const padding = 7 * DAY_MS;
  return {
    min: new Date(min.getTime() - padding),
    max: new Date(max.getTime() + padding),
  };
};

const buildTicks = (min, max, timeScale) => {
  const unitMs = getScaleUnitMs(timeScale);
  const ticks = [];
  let cursor = new Date(min);
  cursor.setHours(0, 0, 0, 0);

  while (cursor <= max) {
    ticks.push(new Date(cursor));
    cursor = new Date(cursor.getTime() + unitMs);
  }
  return ticks;
};

const formatTickLabel = (date, timeScale) => {
  if (timeScale === "month") {
    return date.toLocaleDateString(SCHEDULE_DATE_LOCALE, {
      month: "short",
      year: "numeric",
    });
  }
  if (timeScale === "week") {
    const end = new Date(date.getTime() + 6 * DAY_MS);
    const startLabel = date.toLocaleDateString(SCHEDULE_DATE_LOCALE, {
      month: "short",
      day: "numeric",
    });
    const endLabel = end.toLocaleDateString(SCHEDULE_DATE_LOCALE, {
      day: "numeric",
    });
    return `${startLabel} - ${endLabel}`;
  }
  return date.toLocaleDateString(SCHEDULE_DATE_LOCALE, {
    month: "short",
    day: "numeric",
  });
};

const getBarColor = (row, showCriticalPath) => {
  if (row.isSummary) return "#334155";
  if (showCriticalPath && row.isCritical) return "#ea580c";
  return "#2563eb";
};

const buildDependencyPath = (fromPoint, toPoint) => {
  const midX = fromPoint.x + Math.max(24, (toPoint.x - fromPoint.x) / 2);
  return `M ${fromPoint.x} ${fromPoint.y} H ${midX} V ${toPoint.y} H ${toPoint.x}`;
};

const ScheduleGanttChart = ({
  rows,
  dependencies = [],
  showCriticalPath,
  showPercentComplete,
  showDependencies,
  timeScale,
  scrollHeight,
  scrollRef,
  onScroll,
}) => {
  const headerTrackRef = useRef(null);

  const datedRows = useMemo(
    () => rows.filter((row) => row.start && row.end),
    [rows]
  );

  const rowIndexByKey = useMemo(() => {
    const map = new Map();
    rows.forEach((row, index) => map.set(row.key, index));
    return map;
  }, [rows]);

  const { ticks, timelineWidth, scaleX } = useMemo(() => {
    const range = getTimeRange(datedRows);
    const tickList = buildTicks(range.min, range.max, timeScale);
    const pxPerUnit = timeScale === "day" ? 28 : timeScale === "week" ? 96 : 120;
    const width = Math.max(MIN_TIMELINE_WIDTH, tickList.length * pxPerUnit);
    const span = range.max.getTime() - range.min.getTime() || DAY_MS;

    return {
      ticks: tickList,
      timelineWidth: width,
      scaleX: (date) =>
        ((date.getTime() - range.min.getTime()) / span) * width,
    };
  }, [datedRows, timeScale]);

  const dependencyPaths = useMemo(() => {
    if (!showDependencies) return [];

    return dependencies
      .map((dep) => {
        const fromIndex = rowIndexByKey.get(dep.from);
        const toIndex = rowIndexByKey.get(dep.to);
        if (fromIndex === undefined || toIndex === undefined) return null;

        const fromRow = rows[fromIndex];
        const toRow = rows[toIndex];
        if (!fromRow?.end || !toRow?.start) return null;

        const fromPoint = {
          x: scaleX(fromRow.end),
          y: fromIndex * ROW_HEIGHT + ROW_HEIGHT / 2,
        };
        const toPoint = {
          x: scaleX(toRow.start),
          y: toIndex * ROW_HEIGHT + ROW_HEIGHT / 2,
        };

        return {
          key: `${dep.from}-${dep.to}`,
          d: buildDependencyPath(fromPoint, toPoint),
        };
      })
      .filter(Boolean);
  }, [dependencies, showDependencies, rowIndexByKey, rows, scaleX]);

  const bodyHeight = Math.max(rows.length * ROW_HEIGHT, ROW_HEIGHT);

  const handleBodyScroll = useCallback(
    (event) => {
      if (headerTrackRef.current) {
        headerTrackRef.current.scrollLeft = event.target.scrollLeft;
      }
      onScroll?.(event);
    },
    [onScroll]
  );

  if (datedRows.length === 0) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Empty description="No dated tasks to display" />
      </div>
    );
  }

  const tickElements = ticks.map((tick) => {
    const x = scaleX(tick);
    return (
      <g key={tick.toISOString()}>
        <line x1={x} y1={0} x2={x} y2={HEADER_HEIGHT} stroke="#e5e7eb" />
        <text x={x + 4} y={24} fill="#595959" fontSize={11}>
          {formatTickLabel(tick, timeScale)}
        </text>
      </g>
    );
  });

  const gridLines = ticks.map((tick) => {
    const x = scaleX(tick);
    return (
      <line
        key={`grid-${tick.toISOString()}`}
        x1={x}
        y1={0}
        x2={x}
        y2={bodyHeight}
        stroke="#f0f0f0"
      />
    );
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        ref={headerTrackRef}
        className="shrink-0 overflow-hidden border-b border-slate-100 bg-slate-50"
      >
        <svg
          className="block"
          width={timelineWidth}
          height={HEADER_HEIGHT}
          aria-hidden
        >
          <rect x={0} y={0} width={timelineWidth} height={HEADER_HEIGHT} fill="#fafafa" />
          {tickElements}
        </svg>
      </div>

      <div
        ref={scrollRef}
        onScroll={handleBodyScroll}
        className="min-h-0 flex-1 overflow-auto pr-1 pb-[18px] [scrollbar-gutter:stable_both-edges] box-content"
        style={{ height: scrollHeight, maxHeight: scrollHeight }}
      >
        <svg
          className="block"
          width={timelineWidth}
          height={bodyHeight}
          role="img"
          aria-label="Schedule Gantt chart"
        >
          {gridLines}

          {rows.map((row, index) => {
            if (!row.start || !row.end) {
              return (
                <rect
                  key={`${row.key}-placeholder`}
                  x={0}
                  y={index * ROW_HEIGHT}
                  width={timelineWidth}
                  height={ROW_HEIGHT}
                  fill="transparent"
                />
              );
            }

            const y = index * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2;
            const x = scaleX(row.start);
            const width = Math.max(scaleX(row.end) - x, row.isSummary ? 4 : 2);
            const color = getBarColor(row, showCriticalPath);
            const isMilestone =
              row.taskType === "TT_Mile" || row.taskType === "TT_FinMile";
            const progress =
              showPercentComplete && !row.isSummary && !isMilestone
                ? Math.min(100, Math.max(0, row.percentComplete ?? 0))
                : 0;

            if (isMilestone) {
              const cx = scaleX(row.start);
              const cy = index * ROW_HEIGHT + ROW_HEIGHT / 2;
              const size = 7;
              return (
                <polygon
                  key={row.key}
                  points={`${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`}
                  fill={showCriticalPath && row.isCritical ? "#ea580c" : "#7c3aed"}
                />
              );
            }

            return (
              <g key={row.key}>
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={BAR_HEIGHT}
                  rx={row.isSummary ? 2 : 3}
                  fill={color}
                  opacity={row.isSummary ? 0.85 : 1}
                />
                {progress > 0 && (
                  <rect
                    x={x}
                    y={y}
                    width={(width * progress) / 100}
                    height={BAR_HEIGHT}
                    rx={3}
                    fill="#059669"
                    opacity={0.82}
                  />
                )}
              </g>
            );
          })}

          {dependencyPaths.map((dep) => (
            <path
              key={dep.key}
              d={dep.d}
              fill="none"
              stroke="#595959"
              strokeWidth={1.2}
              markerEnd="url(#schedule-arrow)"
            />
          ))}

          <defs>
            <marker
              id="schedule-arrow"
              markerWidth="8"
              markerHeight="8"
              refX="7"
              refY="4"
              orient="auto"
            >
              <path d="M0,0 L8,4 L0,8 Z" fill="#595959" />
            </marker>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default ScheduleGanttChart;
